import React, { forwardRef, useEffect, useId, useMemo, useRef } from 'react'

import { mergeRefs } from 'utils/ref_utils'

import { CheckboxInput } from './CheckboxInput'

export interface CheckboxTypes extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  description?: React.ReactNode;
  disabled?: boolean;
  error?: React.ReactNode;
  indeterminate?: boolean;
  label?: React.ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxTypes>(({ description, error, id, indeterminate, label, ...props }, forwardedRef) => {
  const ref = useRef<HTMLInputElement>(null)
  const autogeneratedId = useId()
  const descriptionId = useId()
  const errorMessageId = useId()
  const inputId = id || autogeneratedId

  const displayDescription = !!description

  const describedBy = useMemo(
    () =>
      [displayDescription && descriptionId, error && errorMessageId]
        .filter(Boolean)
        .join(' '),
    [error, displayDescription]
  )

  useEffect(() => {
    if (ref.current !== null) {
      ref.current.indeterminate = !!indeterminate
    }
  }, [indeterminate])

  return (
    <div className='group inline-flex items-baseline'>
      <CheckboxInput
        {...props}
        aria-describedby={describedBy}
        aria-invalid={!!error}
        id={inputId}
        indeterminate={indeterminate}
        ref={mergeRefs(ref, forwardedRef)}
      />
      {!!label && (
        <div className='space-y-1 relative -top-[0.0625rem]'>
          <label
            className='pl-3 block body text-gray-800 group-has-[:disabled]:text-gray-500'
            htmlFor={inputId}
          >
            {label}
          </label>
          {!!description && (
            <p className='pl-3 text-gray-500 body-sm' id={descriptionId}>
              {description}
            </p>
          )}
          {!!error && (
            <p className='pl-3 text-red-500 body-sm' id={errorMessageId}>
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  )
})
